// src/components/Blog.js
import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { ChevronLeftIcon, CalendarIcon, UserIcon, TagIcon } from '@heroicons/react/24/outline';

import BlogIcon from '../assets/Blog Default.png';

export default function Blog({ user, onNavigateToEditor, isAuthor }) {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'fitness', 'nutrition', 'mindset'

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      let query = supabase
        .from('blog_posts')
        .select(`
          *,
          author:user_profiles!blog_posts_author_id_fkey(user_id)
        `)
        .eq('published', true)
        .order('published_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('category', filter);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Fetch author emails separately
      const postsWithAuthors = await Promise.all(
        (data || []).map(async (post) => {
          if (post.author?.user_id) {
            const { data: userData } = await supabase.auth.admin.getUserById(post.author.user_id);
            return {
              ...post,
              author_email: userData?.user?.email || 'Anonymous',
              author_name: post.author_name || userData?.user?.email?.split('@')[0] || 'Anonymous'
            };
          }
          return {
            ...post,
            author_email: 'Anonymous',
            author_name: post.author_name || 'Anonymous'
          };
        })
      );

      setPosts(postsWithAuthors);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load blog posts');
      
      // Fallback: try fetching without join
      try {
        const { data, error: fallbackError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('published', true)
          .order('published_at', { ascending: false });

        if (fallbackError) throw fallbackError;
        
        setPosts((data || []).map(post => ({
          ...post,
          author_email: 'Anonymous',
          author_name: post.author_name || 'Anonymous'
        })));
        setError('');
      } catch (fallbackErr) {
        console.error('Fallback fetch also failed:', fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'fitness':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'nutrition':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'mindset':
        return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getReadingTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readingTime} min read`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center">
          <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  // Single post view
  if (selectedPost) {
    return (
      <div className="space-y-6">
        {/* Back button */}
        <button
          onClick={() => setSelectedPost(null)}
          className="flex items-center text-purple-300 hover:text-white transition-colors"
        >
          <ChevronLeftIcon className="h-5 w-5 mr-2" />
          Back to all posts
        </button>

        {/* Post content */}
        <article className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6 sm:p-8">
          {/* Post header */}
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getCategoryColor(selectedPost.category)}`}>
                {selectedPost.category}
              </span>
              <span className="text-purple-300 text-sm flex items-center">
                <CalendarIcon className="h-4 w-4 mr-1" />
                {formatDate(selectedPost.published_at)}
              </span>
              <span className="text-purple-300 text-sm">
                {getReadingTime(selectedPost.content)}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {selectedPost.title}
            </h1>

            <div className="flex items-center text-purple-300 text-sm">
              <UserIcon className="h-4 w-4 mr-2" />
              <span>By {selectedPost.author_name}</span>
            </div>

            {selectedPost.excerpt && (
              <p className="text-xl text-purple-200 mt-4 italic">
                {selectedPost.excerpt}
              </p>
            )}
          </header>

          {/* Featured image */}
          {selectedPost.featured_image && (
            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={selectedPost.featured_image}
                alt={selectedPost.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Post content */}
          <div className="prose prose-invert prose-purple max-w-none">
            <div className="text-purple-100 leading-relaxed whitespace-pre-wrap">
              {selectedPost.content}
            </div>
          </div>

          {/* Tags */}
          {selectedPost.tags && selectedPost.tags.length > 0 && (
            <div className="mt-8 pt-6 border-t border-white/20">
              <div className="flex flex-wrap items-center gap-2">
                <TagIcon className="h-5 w-5 text-purple-400" />
                {selectedPost.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-white/10 rounded-full text-purple-300 text-sm"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    );
  }

  // Posts listing view
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2 flex items-center">
              <img src={BlogIcon} alt="Blog" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
              Blog
            </h2>
            <p className="text-purple-300">
              Tips, insights, and inspiration for your fitness journey
            </p>
          </div>

          {isAuthor && (
            <button
              onClick={onNavigateToEditor}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105"
            >
              ✏️ Write New Post
            </button>
          )}
        </div>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-3">
        {['all', 'fitness', 'nutrition', 'mindset'].map((category) => (
          <button
            key={category}
            onClick={() => setFilter(category)}
            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
              filter === category
                ? 'bg-purple-600 text-white'
                : 'bg-white/10 text-purple-300 hover:bg-white/20'
            }`}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
          <p className="text-red-200">{error}</p>
        </div>
      )}

      {/* Posts grid */}
      {posts.length === 0 ? (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-12 text-center">
          <div className="text-6xl mb-4">📚</div>
          <h3 className="text-xl font-bold text-white mb-2">No posts yet</h3>
          <p className="text-purple-300">
            {filter === 'all' 
              ? 'Check back soon for inspiring content!'
              : `No posts in the ${filter} category yet.`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post) => (
            <article
              key={post.id}
              className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6 hover:bg-white/15 transition-all duration-200 cursor-pointer transform hover:scale-105"
              onClick={() => setSelectedPost(post)}
            >
              {/* Featured image */}
              {post.featured_image && (
                <div className="mb-4 rounded-lg overflow-hidden">
                  <img
                    src={post.featured_image}
                    alt={post.title}
                    className="w-full h-48 object-cover"
                  />
                </div>
              )}

              {/* Category badge */}
              <div className="flex items-center justify-between mb-3">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getCategoryColor(post.category)}`}>
                  {post.category}
                </span>
                <span className="text-purple-400 text-xs">
                  {getReadingTime(post.content)}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">
                {post.title}
              </h3>

              {/* Excerpt */}
              {post.excerpt && (
                <p className="text-purple-200 text-sm mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
              )}

              {/* Meta */}
              <div className="flex items-center justify-between text-xs text-purple-300">
                <span className="flex items-center">
                  <UserIcon className="h-4 w-4 mr-1" />
                  {post.author_name}
                </span>
                <span className="flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-1" />
                  {formatDate(post.published_at)}
                </span>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
